<script setup lang="ts">
import { ChampionshipRegistrationPriceMode } from "@/model/championship/championship-registration-price-mode"
import type { ChampionshipRegistrationTeam } from "@/model/championship/championship-registration-team"
import type { ChampionshipRegistrationTeamLog } from "@/model/championship/championship-registration-team-log"
import { defineTranslations } from "@/utils/defineTranslations"
import { $shortdatetime } from "@/vf"
import { computed, ref, watch } from "vue"
import type { ChampionshipRegistrationApproveData, RegistrationPermissions } from "../championship-registration"
import ChampionshipRegistrationApprovalStateIcon from "./ChampionshipRegistrationApprovalStateIcon.vue"
import ChampionshipRegistrationTeamList from "./ChampionshipRegistrationTeamList.vue"

const props = defineProps<{
    data: ChampionshipRegistrationApproveData
    teams: ChampionshipRegistrationTeam[]
    viewMode: "customer" | "admin"
    permissions?: RegistrationPermissions
}>()

export type ApprovalResult = Record<
    string,
    {
        approvalState: string
        priceMode: ChampionshipRegistrationPriceMode
        paidBidFreeAthletes: number
        paidBidFreeEntourages: number
    }
>
const model = defineModel<ApprovalResult>({ default: () => ({}) })

/**
 * The decisions this component collects. The model cannot hold them: as long as a v-model is bound, Vue does not read
 * back what was just written into it, so the first render would see an empty record. Every team starts out with the
 * decision it currently has, so only actual changes have to be made.
 */
const result = ref<ApprovalResult>({})

watch(
    () => props.data.teams,
    () => {
        result.value = Object.fromEntries(
            props.data.teams.map(team => [
                team.id!,
                {
                    approvalState: team.approvalState ?? "undecided",
                    priceMode: team.priceMode ?? ChampionshipRegistrationPriceMode.Regular,
                    paidBidFreeAthletes: team.paidBidFreeAthletes ?? 0,
                    paidBidFreeEntourages: team.paidBidFreeEntourages ?? 0,
                },
            ]),
        )
    },
    { immediate: true },
)

// Hand a copy to the parent on every change, so it does not depend on the identity of our object
watch(
    result,
    () => {
        model.value = Object.fromEntries(
            Object.entries(result.value).map(([teamId, decision]) => [teamId, { ...decision }]),
        )
    },
    { deep: true, immediate: true },
)

/*─────────────────────────────────────┐
│  validation                          │
└─────────────────────────────────────*/
const originalStates = ref<Record<string, string>>(
    Object.fromEntries(props.data.teams.map(team => [team.id, team.approvalState])),
)
const allTeamsDecided = computed(() => {
    return props.teams.every(
        team =>
            result.value[team.id!]?.approvalState !== undefined && result.value[team.id!].approvalState !== "undecided",
    )
})

const trans = defineTranslations({
    log_time: {
        de: "Zeitpunkt",
        en: "Time",
    },
    log_action: {
        de: "Aktion",
        en: "Action",
    },
    price_mode: {
        de: "Preisberechnung",
        en: "Price calculation",
    },
    price_mode_regular: {
        de: "Reguläre Berechnung",
        en: "Regular calculation",
    },
    price_mode_free_of_charge: {
        de: "Kostenlose Teilnahme",
        en: "Free of charge participation",
    },
    price_mode_paid_bid_winner: {
        de: "Paid Bid Winner",
        en: "Paid bid winner",
    },
    paid_bid_free_athletes: {
        de: "Kostenlose Athleten (aktive und Ersatzpersonen)",
        en: "Free athletes (active and alternates)",
    },
    paid_bid_free_entourages: {
        de: "Kostenlose Entourage (Betreuer und Coaches)",
        en: "Free entourage (supervisors and coaches)",
    },
})

function isUnreviewed(
    log: ChampionshipRegistrationTeamLog,
    index: number,
    team: ChampionshipRegistrationTeam,
): boolean {
    if (
        !(
            [
                "CategoryChanged",
                "TeamNameChanged",
                "NumberOfParticipantsChanged",
                "NumberOfCoachesChanged",
                "NumberOfSupervisorsChanged",
                "NumberOfAlternatesChanged",
            ].includes(log.action) && team.approvalState === "approved_modified"
        )
    ) {
        return false
    }

    // Find the most recent (first in array) StatusChanged with context.new = 'Approved'
    const lastApprovedIndex = team.logs!.findIndex(l => l.action === "StatusChanged" && l.context?.new === "Approved")

    // Only highlight if this log comes before the last approved status change
    return lastApprovedIndex !== -1 && index < lastApprovedIndex
}
</script>
<template>
    <ChampionshipRegistrationTeamList
        :registration-id="data.registrationId"
        :teams="teams"
        :show-price="false"
        :view-mode="props.viewMode"
        :permissions="props.permissions"
    >
        <template #secondaryRow="{ team, colspan }">
            <tr v-if="team.logs?.length">
                <td :colspan="colspan" class="">
                    <table class="table table-borderless table-sm">
                        <thead>
                            <tr>
                                <th>{{ trans("log_time") }}</th>
                                <th>{{ trans("log_action") }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(log, index) in team.logs" :key="log.id">
                                <template v-if="isUnreviewed(log, index, team)">
                                    <td>
                                        <div class="py-alert">{{ $shortdatetime(log.time) }}</div>
                                    </td>
                                    <td>
                                        <div class="alert alert-outer alert-warning mb-0">
                                            {{ $t(`@championship:registration.log.${log.action}`, log.context) }}
                                        </div>
                                    </td>
                                </template>
                                <template v-else>
                                    <td>{{ $shortdatetime(log.time) }}</td>
                                    <td>
                                        {{ $t(`@championship:registration.log.${log.action}`, log.context) }}
                                    </td>
                                </template>
                            </tr>
                        </tbody>
                    </table>
                </td>
            </tr>
            <tr v-if="result[team.id!]">
                <td colspan="6" class="bg-light pl-5">
                    <div class="mb-3">Bitte setze einen Status:</div>

                    <div class="pl-4">
                        <label class="d-block" v-if="originalStates[team.id!] === 'waiting_list'">
                            <ChampionshipRegistrationApprovalStateIcon state="waiting_list_approved" />
                            <input
                                type="radio"
                                v-model="result[team.id!].approvalState"
                                value="waiting_list_approved"
                                class="ml-2"
                            />
                            Warteliste freigegeben
                        </label>
                        <label class="d-block" v-else>
                            <ChampionshipRegistrationApprovalStateIcon state="approved" />
                            <input
                                type="radio"
                                v-model="result[team.id!].approvalState"
                                value="approved"
                                class="ml-2"
                            />
                            freigeben
                        </label>
                        <label class="d-block">
                            <ChampionshipRegistrationApprovalStateIcon state="waiting_list" />
                            <input
                                type="radio"
                                v-model="result[team.id!].approvalState"
                                value="waiting_list"
                                class="ml-2"
                            />
                            Warteliste
                        </label>
                        <label class="d-block">
                            <ChampionshipRegistrationApprovalStateIcon state="declined" />
                            <input
                                type="radio"
                                v-model="result[team.id!].approvalState"
                                value="declined"
                                class="ml-2"
                            />
                            ablehnen
                        </label>
                        <label class="d-block">
                            <ChampionshipRegistrationApprovalStateIcon state="canceled" />
                            <input
                                type="radio"
                                v-model="result[team.id!].approvalState"
                                value="canceled"
                                class="ml-2"
                            />
                            storniert
                        </label>
                    </div>

                    <div v-if="props.viewMode === 'admin'" class="mt-3 pl-4" style="max-width: 25rem">
                        <div class="form-group">
                            <label :for="`price-mode-${team.id}`">{{ trans("price_mode") }}</label>
                            <select
                                :id="`price-mode-${team.id}`"
                                class="form-control"
                                v-model="result[team.id!].priceMode"
                            >
                                <option :value="ChampionshipRegistrationPriceMode.Regular">
                                    {{ trans("price_mode_regular") }}
                                </option>
                                <option :value="ChampionshipRegistrationPriceMode.FreeOfCharge">
                                    {{ trans("price_mode_free_of_charge") }}
                                </option>
                                <option :value="ChampionshipRegistrationPriceMode.PaidBidWinner">
                                    {{ trans("price_mode_paid_bid_winner") }}
                                </option>
                            </select>
                        </div>

                        <template v-if="result[team.id!].priceMode === ChampionshipRegistrationPriceMode.PaidBidWinner">
                            <div class="form-group">
                                <label :for="`paid-bid-free-athletes-${team.id}`">
                                    {{ trans("paid_bid_free_athletes") }}
                                </label>
                                <input
                                    :id="`paid-bid-free-athletes-${team.id}`"
                                    type="number"
                                    min="0"
                                    class="form-control"
                                    v-model.number="result[team.id!].paidBidFreeAthletes"
                                />
                            </div>
                            <div class="form-group">
                                <label :for="`paid-bid-free-entourages-${team.id}`">
                                    {{ trans("paid_bid_free_entourages") }}
                                </label>
                                <input
                                    :id="`paid-bid-free-entourages-${team.id}`"
                                    type="number"
                                    min="0"
                                    class="form-control"
                                    v-model.number="result[team.id!].paidBidFreeEntourages"
                                />
                            </div>
                        </template>
                    </div>
                </td>
            </tr>
        </template>
    </ChampionshipRegistrationTeamList>

    <slot name="bottom" v-bind="{ allTeamsDecided, result }"></slot>
</template>
